import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteWarehouseMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, usePermissions} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function Warehouses() {
  const navigate = useNavigate();

  const [warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const [deleteWarehouse] = useDeleteWarehouseMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;
        navigate(composePath(settingsRoutes.warehousesDetail, {params: {id}}));
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];
        const requestBody = {warehouseId: ids};

        openDeleteDialog({
          text: i18n.t('entity.warehouse.labels.deleteSelectedItemsPrompt'),
          onConfirm: () =>
            deleteWarehouse({body: requestBody})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(noop);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.warehouses'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newWarehouse'),
        onClick: () => navigate(settingsRoutes.warehousesCreate),
        isDisabled: !warehouseDictionaryCreate,
        tooltip: !warehouseDictionaryCreate
          ? i18n.t('entity.warehouse.labels.missingWarehouseDictionaryCreatePermission')
          : undefined,
        'data-testid': testIds.settings.warehousesList('actions.new'),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.warehouses')} />
      <SettingsTemplate header={header} data-testid="settings-warehouses">
        <DmsDataGrid
          gridCode="warehouse"
          actionCallback={actionCallback}
          emptyState={{
            headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
          }}
          data-testid={testIds.settings.warehousesList('warehouses')}
        />
      </SettingsTemplate>
    </>
  );
}
